/**
 * Utilidades del árbol de ubicaciones (edificio > planta > sala > fila).
 *
 * Vive aquí y no en cada componente porque el listado y la ficha tienen que
 * pintar y validar el MISMO árbol: si el listado ordena en profundidad y la
 * ficha ofrece otro conjunto de padres válidos, el usuario puede acabar
 * creando un ciclo. Ambas pantallas tiran de estas funciones.
 */

export const TYPE_ICONS = { building: '🏢', office: '🏬', warehouse: '🏭', floor: '📶', room: '🚪', row: '📦' };

export const TYPE_LABELS = {
  building: 'Edificio',
  office: 'Oficina',
  warehouse: 'Nave',
  floor: 'Planta',
  room: 'Sala',
  row: 'Fila',
};

export const typeLabel = (type) => TYPE_LABELS[type] || type || '';

export const typeIcon = (type) => TYPE_ICONS[type] || '📍';

/** Orden en profundidad con `_depth` por fila, para pintar el árbol. */
export function buildTreeFlat(locations, parentId = null, depth = 0) {
  const children = locations
    .filter((l) => (l.parent_id ?? null) === parentId)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
  const result = [];
  for (const loc of children) {
    result.push({ ...loc, _depth: depth });
    result.push(...buildTreeFlat(locations, loc.id, depth + 1));
  }
  return result;
}

/**
 * Ids de una ubicación y todos sus descendientes.
 * Al mover o reasignar el padre hay que excluir este conjunto: colgar una
 * ubicación de una de sus hijas dejaría un ciclo (A > B > A).
 */
export function subtreeIds(locations, rootId) {
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const loc of locations) {
      if (loc.parent_id != null && ids.has(loc.parent_id) && !ids.has(loc.id)) {
        ids.add(loc.id);
        grew = true;
      }
    }
  }
  return ids;
}

/** Padres válidos para una ubicación: activos y ni ella misma ni sus descendientes. */
export function eligibleParents(locations, locationId) {
  const forbidden = locationId != null ? subtreeIds(locations, locationId) : new Set();
  return locations
    .filter((l) => l.is_active && !forbidden.has(l.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
}

/* ── Raíz de una ubicación y agrupación de equipos ─────────────────────────
   Para conectar equipos no basta con el mismo rack: dos patch panels de
   plantas distintas deben poder unirse si cuelgan de la misma raíz (la sede).
   Estas funciones resuelven esa raíz y preparan los destinos agrupados. */

/** Índice id → ubicación (las claves siempre como texto). */
export function buildLocationMap(locations = []) {
  const map = new Map();
  (locations || []).forEach((loc) => {
    if (loc?.id != null) map.set(String(loc.id), loc);
  });
  return map;
}

/** Id de la ubicación raíz (el ancestro sin padre). */
export function locationRootId(locationMap, locationId) {
  if (locationId == null) return null;
  let current = locationMap.get(String(locationId));
  if (!current) return null;

  const visitadas = new Set();
  while (current?.parent_id != null) {
    const clave = String(current.id);
    // Datos corruptos (un ciclo) no deben colgar la interfaz.
    if (visitadas.has(clave)) break;
    visitadas.add(clave);
    const parent = locationMap.get(String(current.parent_id));
    if (!parent) break;
    current = parent;
  }
  return current?.id != null ? String(current.id) : null;
}

/** Ruta legible: «Sede Central › Planta 2 › Sala técnica». */
export function locationPath(locationMap, locationId) {
  if (locationId == null) return '';
  const partes = [];
  let current = locationMap.get(String(locationId));
  const visitadas = new Set();

  while (current) {
    const clave = String(current.id);
    if (visitadas.has(clave)) break;
    visitadas.add(clave);
    if (current.name) partes.unshift(current.name);
    current = current.parent_id != null
      ? locationMap.get(String(current.parent_id))
      : null;
  }
  return partes.join(' › ');
}

/**
 * Opciones de filtro con la ruta completa, en orden de organigrama.
 *
 * Evita que cada listado invente su propio `<select>`: con muchas sedes, dos
 * «Sala 1» de plantas distintas son indistinguibles si solo se ve el nombre.
 *
 * @param {Array} locations  árbol completo (`api.getLocations`)
 * @param {Object} [opts]
 * @param {Iterable} [opts.onlyIds]  limita a las ubicaciones realmente usadas
 */
export function locationPathOptions(locations = [], { onlyIds } = {}) {
  const mapa = buildLocationMap(locations);
  const filtro = onlyIds ? new Set([...onlyIds].filter((id) => id != null).map(String)) : null;
  return buildTreeFlat(locations)
    .filter((loc) => !filtro || filtro.has(String(loc.id)))
    .map((loc) => ({ value: String(loc.id), label: locationPath(mapa, loc.id) || loc.name }));
}

/**
 * Agrupa elementos por ubicación + rack, en orden alfabético.
 * Devuelve [{ key, label, elements }], listo para pintar títulos dentro del
 * `<select>` sin depender de `<optgroup>` (cuyo fondo nativo rompía el tema
 * oscuro en Windows).
 */
export function groupElementsByLocation(locationMap, elements = []) {
  const grupos = new Map();

  (elements || []).forEach((el) => {
    const ruta = locationPath(locationMap, el?.location_id) || 'Sin ubicación';
    const rack = el?.rack_name ? ` › ${el.rack_name}` : '';
    const key = `${ruta}${rack}`;
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key).push(el);
  });

  return [...grupos.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'es', { numeric: true }))
    .map(([label, list]) => ({
      key: label,
      label,
      elements: [...list].sort((a, b) => String(a.name || a.code || '')
        .localeCompare(String(b.name || b.code || ''), 'es', { numeric: true })),
    }));
}
